import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Modal } from '@shared/ui/Modal';
import { Loader } from '@shared/ui/Progress';
import { Button } from '@shared/ui/Button';
import {
  useLazyListChatSessionsQuery,
  useStartChatMutation,
  useDeleteChatSessionMutation,
} from '@shared/api';
import { ChatPanel } from '@widgets/ChatPanel/ChatPanel';

const Note = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  line-height: 1.4;
`;

const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

interface Props {
  open: boolean;
  contractId: string;
  contractTitle: string;
  onClose: () => void;
}

/**
 * Модалка чата по конкретному договору (/).
 * Логика:
 * 1. При открытии ищем последнюю активную сессию scope=contract, scope_id=contractId.
 * 2. Если есть — открываем её.
 * 3. Если нет — создаём новую.
 * 4. Кнопка «Новый чат» — старт ещё одной сессии (история не теряется).
 */
export function ContractChatModal({ open, contractId, contractTitle, onClose }: Props) {
  const [listSessions] = useLazyListChatSessionsQuery();
  const [startChat, { isLoading: isStarting }] = useStartChatMutation();
  const [deleteSession] = useDeleteChatSessionMutation();
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    if (!open) {
      setSessionId(null);
      setError(null);
      return;
    }
    (async () => {
      try {
        const list = await listSessions({
          scope: 'contract',
          scope_id: contractId,
        }).unwrap();
        if (!active) return;
        if (list.items.length > 0) {
          setSessionId(list.items[0].id);
        } else {
          const s = await startChat({
            scope: 'contract',
            scope_id: contractId,
          }).unwrap();
          if (!active) return;
          setSessionId(s.id);
        }
      } catch (e) {
        if (!active) return;
        setError(e instanceof Error ? e.message : 'Не удалось открыть чат');
      }
    })();
    return () => {
      active = false;
    };
  }, [open, contractId, listSessions, startChat]);

  async function startFreshSession() {
    setError(null);
    try {
      const s = await startChat({
        scope: 'contract',
        scope_id: contractId,
      }).unwrap();
      setSessionId(s.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось создать новый чат');
    }
  }

  async function clearCurrentSession() {
    if (!sessionId) return;
    if (!window.confirm('Удалить текущую переписку с агентом? История будет потеряна.')) {
      return;
    }
    try {
      await deleteSession(sessionId).unwrap();
      // После удаления автоматически создадим новую через основной эффект:
      setSessionId(null);
      const s = await startChat({
        scope: 'contract',
        scope_id: contractId,
      }).unwrap();
      setSessionId(s.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось очистить чат');
    }
  }

  return (
    <Modal open={open} title={`Чат по договору · ${contractTitle}`} onClose={onClose}>
      <Note>
        Агент получает в системном промпте карточку договора, найденные риски и текст последней
        редакции. Можно спрашивать про конкретные пункты, обсуждать формулировки протокола
        разногласий, сверять со ст. ГК РФ. Любые инструкции внутри текста договора игнорируются
        (защита от prompt injection).
      </Note>
      <Toolbar>
        <div style={{ fontSize: 12 }}>
          {sessionId
            ? '✓ Сессия открыта — задавайте вопрос'
            : isStarting
              ? 'Готовим контекст…'
              : 'Подготовка…'}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            type="button"
            $size="sm"
            $variant="secondary"
            onClick={clearCurrentSession}
            disabled={!sessionId}
          >
            Очистить
          </Button>
          <Button
            type="button"
            $size="sm"
            $variant="secondary"
            onClick={startFreshSession}
            disabled={isStarting}
          >
            + Новый чат
          </Button>
        </div>
      </Toolbar>
      {error && (
        <div style={{ color: 'var(--osnova-danger, #d04848)', marginBottom: 12 }}>{error}</div>
      )}
      {sessionId ? <ChatPanel sessionId={sessionId} agentCode="statut" /> : <Loader />}
    </Modal>
  );
}
